import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Alert from '../components/Alert';
import { useForm } from '../hooks/useForm';

const ResetPassword = () => {
    const { t } = useTranslation();
    const { token } = useParams();
    const navigate = useNavigate();
    const { formData, handleChange } = useForm({
        password: '',
        confirmPassword: '',
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError(t('validation.passwordMismatch'));
            return;
        }

        if (formData.password.length < 8) {
            setError(t('validation.passwordTooShort'));
            return;
        }

        setLoading(true);

        try {
            await axios.put(`/api/auth/reset-password/${token}`, {
                password: formData.password,
            });
            navigate('/login', {
                state: { message: t('auth.resetPassword.successMessage') },
            });
        } catch (err) {
            setError(err.response?.data?.message || t('auth.resetPassword.resetError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-transition min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.resetPassword.title')}</h1>
                    <p className="text-gray-600">
                        {t('auth.resetPassword.subtitle')}
                    </p>
                </div>

                <div className="card">
                    <Alert type="error" message={error} onDismiss={() => setError('')} />

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('auth.resetPassword.newPassword')}
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                required
                                value={formData.password}
                                onChange={handleChange}
                                className="input-field"
                                placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                                autoFocus
                            />
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('auth.resetPassword.confirmPassword')}
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                required
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className="input-field"
                                placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                            />
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800 font-semibold mb-2">
                                {t('auth.resetPassword.requirementsTitle')}
                            </p>
                            <ul className="text-sm text-blue-700 space-y-1 ml-4 list-disc">
                                <li>{t('auth.resetPassword.requirement1')}</li>
                                <li>{t('auth.resetPassword.requirement2')}</li>
                                <li>{t('auth.resetPassword.requirement3')}</li>
                                <li>{t('auth.resetPassword.requirement4')}</li>
                            </ul>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? t('auth.resetPassword.resetting') : t('auth.resetPassword.submitButton')}
                        </button>
                    </form>

                    <div className="mt-6 text-center">
                        <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                            {t('auth.forgotPassword.backToLogin')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
